import type { FullFormaPullResult } from '@enersu/core-orchestrator';

/**
 * Persists a full Forma snapshot to Supabase.
 *
 * Writes go straight to the Supabase Data API (PostgREST) with the publishable
 * anon key, so the extension needs no heavy SDK in the bundle. The target table
 * has an insert-only row-level-security policy for the anon role: the key can
 * write snapshots but cannot read them back, keeping pulled geometry private.
 */
export interface SupabaseSnapshotClient {
  saveSnapshot(result: FullFormaPullResult): Promise<SupabaseSaveResult>;
}

export interface SupabaseSaveResult {
  saved: true;
}

export interface CreateSupabaseSnapshotClientOptions {
  /** Project URL, e.g. https://<ref>.supabase.co */
  url: string;
  /** Publishable (anon) API key. */
  anonKey: string;
  /** Destination table. Defaults to forma_snapshots. */
  table?: string;
  fetcher?: typeof fetch;
}

const DEFAULT_TABLE = 'forma_snapshots';

export interface ViteSupabaseEnv {
  VITE_SUPABASE_URL?: string;
  VITE_SUPABASE_ANON_KEY?: string;
  VITE_SUPABASE_SNAPSHOT_TABLE?: string;
}

/**
 * Resolves Supabase client options from a Vite env bag, or undefined when the
 * URL/key are missing or blank. Vite surfaces a present-but-empty env var as ''
 * (not undefined), so blank values are treated as unconfigured — otherwise the
 * panel shows an enabled "Save" button that always fails on an empty URL/key.
 * Pure (env passed in) so the guard is deterministically testable.
 */
export function resolveSupabaseConfig(
  env: ViteSupabaseEnv | undefined,
): CreateSupabaseSnapshotClientOptions | undefined {
  const url = env?.VITE_SUPABASE_URL?.trim();
  const anonKey = env?.VITE_SUPABASE_ANON_KEY?.trim();

  if (!url || !anonKey) {
    return undefined;
  }

  const table = env?.VITE_SUPABASE_SNAPSHOT_TABLE?.trim();

  return { url, anonKey, ...(table ? { table } : {}) };
}

/**
 * Builds a Supabase snapshot client from Vite build-time env, or undefined when
 * the URL/key are not configured (so the UI can disable the action cleanly).
 */
export function createConfiguredSupabaseSnapshotClient(): SupabaseSnapshotClient | undefined {
  const env = (import.meta as ImportMeta & { readonly env?: ViteSupabaseEnv }).env;
  const config = resolveSupabaseConfig(env);

  return config === undefined ? undefined : createSupabaseSnapshotClient(config);
}

export function createSupabaseSnapshotClient(
  options: CreateSupabaseSnapshotClientOptions,
): SupabaseSnapshotClient {
  const baseUrl = options.url.replace(/\/+$/, '');
  const table = options.table ?? DEFAULT_TABLE;
  const fetcher = options.fetcher ?? fetch;

  return {
    async saveSnapshot(result) {
      const response = await fetcher(`${baseUrl}/rest/v1/${table}`, {
        method: 'POST',
        headers: {
          apikey: options.anonKey,
          authorization: `Bearer ${options.anonKey}`,
          'content-type': 'application/json',
          // Insert-only RLS: do not ask PostgREST to return the inserted row.
          prefer: 'return=minimal',
        },
        body: JSON.stringify({
          forma_project_id: result.snapshot.project.formaProjectId ?? null,
          ok_count: result.okCount,
          error_count: result.errorCount,
          unavailable_count: result.unavailableCount,
          api_report: result.apiReport,
          snapshot: result.snapshot,
        }),
      });

      if (!response.ok) {
        throw new Error(
          `Supabase insert failed with ${response.status}${await readErrorDetail(response)}.`,
        );
      }

      return { saved: true };
    },
  };
}

/**
 * Best-effort PostgREST error detail. PostgREST returns a JSON body describing
 * the real cause (RLS violation, malformed payload, 413 too large); surfacing it
 * makes field debugging of failed saves tractable.
 */
async function readErrorDetail(response: Response): Promise<string> {
  try {
    const body = (await response.text()).trim();

    return body.length === 0 ? '' : `: ${body.slice(0, 300)}`;
  } catch {
    return '';
  }
}
